import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { ApplicationState } from '../../../store';
import * as Models from '../../../store/Models/SharedMaterials/DataModel';
import * as SharedMaterialsDatabaseStateController from '../../../store/StateControllers/SharedMaterials/SharedMaterialsDatabaseStateController';
import { ToxnotReactComponentPaths } from '../../core/ToxnotReactComponentPaths';
import { AbstractSharedMaterialDetail, DetailSectionRenderData } from './AbstractSharedMaterialDetail';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { SlidingDrawer } from '../../core/SlidingDrawer';
import { LoadingSpinner, LoadingSpinnerSize } from '../../core/LoadingSpinner';
import CreateAndEditSurveyQuestionsPage from '../../Survey/CreateAndEditSurveyPages/CreateAndEditSurveyQuestionsPage';
import * as SurveyStateController from '../../../store/StateControllers/Survey/SurveyStateController';
import * as BrowserStateModels from "../../../store/Models/SharedMaterials/BrowserStateModels";
import { rowPerPageValue } from '../../../store/StateControllers/SharedMaterials/SharedMaterialsDatabaseStateController';
import { DynamicRegulation } from "../../../store/Models/Compliance/DynamicRegulation";


interface ReduxConnectedState {
    detailState: SharedMaterialsDatabaseStateController.SharedMaterialDetailState;
    sharedMaterialsCoreDataMap: { [sharedMaterialId: string]: Models.SharedMaterialCoreData };
    isLoadingCatalog: boolean;
}

type SharedMaterialDetailProps =
    ReduxConnectedState &
    ToxnotComponentProperties &
    typeof SharedMaterialsDatabaseStateController.actionCreators &
    typeof SurveyStateController.actionCreators &
    RouteComponentProps<{ sharedMaterialId: string }>

export class SharedMaterialDetail extends AbstractSharedMaterialDetail<SharedMaterialDetailProps> {
    private readonly _RowPerPageValue: number = rowPerPageValue;

    // This method is called when the component is first added to the document
    public async componentDidMount() {
        const { sharedMaterialId } = this.props.match.params;
        
        if (sharedMaterialId) {
            this.props.loadSharedMaterialsIfNeeded(this.props.appServices, this.getQueryParameter("accessCode"), 0, '', this._RowPerPageValue, {}, BrowserStateModels.SortTypes.PassportName, BrowserStateModels.SortDirectionType.Ascending, null, sharedMaterialId);
        }
    
        await this.props.loadSharedMaterialDetail(sharedMaterialId, this.props.appServices, this.getQueryParameter("accessCode"));
    }

    // ===============================================================================

    // This method is called when the route parameters change
    public componentDidUpdate() {
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.props.appServices.trackEvent("exchange-viewed-passport");

        this.props.clearSharedMaterialDetailState();
    }

    // ===============================================================================

    protected renderModals() {
        return (
            <div id="send-survey-modal" className="modal fade" tabIndex={-1} role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" id="send-survey-modal-x-close-button" className="close" disabled={this.props.detailState.isSendingSurveys} data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                            <h4 className="modal-title">{this.props.appServices.localizeString("Toxnot is sending your survey")}</h4>
                        </div>
                        <div className="modal-body">
                            {this.props.detailState.isSendingSurveysError &&
                                <div className="alert alert-error m-b-0">
                                    {this.localizeString("Sorry, there was a problem accessing the service. Please try again.")}
                                </div>
                            }
                            {this.props.detailState.isSendingSurveysSuccess &&
                                <div className="alert alert-success m-b-0">
                                    {this.localizeString("Survey sent!")}
                                </div>
                            }
                            {(this.props.detailState.isSendingSurveysSuccess == false && this.props.detailState.isSendingSurveysError == false) &&
                                <LoadingSpinner />
                            }
                        </div>
                        <div className="modal-footer">
                            <button type="button" disabled={this.props.detailState.isSendingSurveys} id="send-survey-modal-close-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Done")}</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ===============================================================================

    protected isLoading() {
        return this.props.detailState.status === SharedMaterialsDatabaseStateController.SharedMaterialDetailStatus.Loading || this.props.isLoadingCatalog;
    }

    // ===============================================================================

    protected peekRenderData(): DetailSectionRenderData {
        const coreData = this.props.sharedMaterialsCoreDataMap[this.props.match.params.sharedMaterialId];
        /**
         * This method will be called once the shared-material is fetched from API. 
         * Api logic is written in mount method
         */
            return {
                coreData: coreData,
                detailBundle: this.props.detailState.detailBundle!,
                company: {
                    ownerId: coreData.ownerId,
                    name: coreData.companyName,
                    logoFileId: null,
                    companyId: coreData.companyId,
                    contactEmail: ""
                }
            };     
    }

    // ===============================================================================

    protected peekCollapsedSections(): string[] {
        return this.props.detailState.collapsedSections;
    }

    // ===============================================================================

    private renderLibraryAction(detailBundle: Models.SharedMaterialDetailResponse, coreData: Models.SharedMaterialCoreData, libraryInventoryId: string | null): JSX.Element | null {
        // check if user shares team onwer id with passport
        if (this.props.appServices.userContext && this.props.appServices.userContext.teamOwnerId === coreData.ownerId) {
            return (
                <button type="button" onClick={e => this.goToLibrarySourceInventoryButton_click(coreData.publicInventoryId)} className="btn btn-default m-r-5">{this.localizeString("Go to Source Material in My Library")}</button>
            );
        }
        // check if has access
        else if (coreData.accessLevel === Models.SharedMaterialAccessType.Public || coreData.hasAccess) {
            return (libraryInventoryId !== null)
                ? 
                    (<>
                    {!detailBundle.isUnClaimed && <button type="button" onClick={e => this.requestInformationButton_click()} id="request-information-passport" className="btn btn-default m-r-5">{this.localizeString("Request Information")}</button>}
                        <button type="button" onClick={e => this.goToLibraryInventoryButton_click(libraryInventoryId)} className="btn btn-default m-r-5">{this.localizeString("Go to Material in My Library")}</button>
                    </>)
                : this.props.appServices.userContext?.isEditor
                    ? 
                        (<>
                        {!detailBundle.isUnClaimed && <button type="button" onClick={e => this.requestInformationButton_click()} id="request-information-passport" className="btn btn-default m-r-5">{this.localizeString("Request Information")}</button>}
                            <button type="button" onClick={e => this.addToLibraryButton_click(coreData.sharedMaterialId)} id="add-bom-to-materials-library-button" className="btn btn-default m-r-5"><span className="glyphicon glyphicon-plus"></span>&nbsp;{this.localizeString("My Materials")}</button>
                        </>)
                    : null
                ;
        }
        // else show request access
        return (
            <a role="button" id="request-access-for-shared-material" onClick={e => this.requestAccessButton_click()} className="btn btn-default m-r-5">{this.localizeString("Request Access")}</a>
        );
    }

    // ===============================================================================

    protected renderTopLevelActionButtons(data: DetailSectionRenderData) {
        const libraryInventoryId = data.detailBundle?.libraryInventoryId ?? null;
        const coreData = data.coreData;
        const detailBundle = data.detailBundle;

        return (
            <>
                <div className="tn-section-col-12">
                    {this.props.detailState.addingState === SharedMaterialsDatabaseStateController.AddToLibraryState.Adding && <div className="alert alert-info">{this.localizeString("Adding...")}</div>}
                    {this.props.detailState.addingState === SharedMaterialsDatabaseStateController.AddToLibraryState.Added && <div className="alert alert-success">{this.localizeString("Successfully added to your library!")}</div>}
                    {this.props.detailState.addingState === SharedMaterialsDatabaseStateController.AddToLibraryState.Failed && <div className="alert alert-danger">{this.localizeString("Sorry, failed to add to library")}</div>}
                    <div className="margin-bottom">
                        {this.props.history.length > 1 &&
                            <button id="back-button" className="btn btn-default" onClick={e => this.props.history.goBack()}>{this.localizeString("Back")}</button>
                        }
                        <div className="pull-right">
                            {this.renderLibraryAction(detailBundle, coreData, libraryInventoryId)}
                            {coreData.accessLevel !== Models.SharedMaterialAccessType.Private && <Link role="button" to={ToxnotReactComponentPaths.ShareMaterial + `/${coreData.sharedMaterialId}`} onClick={e => this.shareMaterialButton_click()} className="btn btn-default"><span className="glyphicon glyphicon-share-alt"></span>&nbsp;{this.localizeString("Share")}</Link>}
                        </div>
                    </div>
                </div>
                {(coreData.accessLevel === Models.SharedMaterialAccessType.Public || coreData.hasAccess) && this.props.appServices.userContext?.teamOwnerId !== coreData.ownerId &&
                    <SlidingDrawer
                        preventCloseOnModalClick={true}
                        hasCta={this.props.detailState.isSlidingDrawerDataLoaded}
                        ctaTitle={this.localizeString("Send Survey")}
                        ctaCallback={this.sendSurvey}
                        drawerTitle={this.getSlidingDrawerTitle(coreData)}
                        onClose={this.slidingDrawer_onClose}
                        openDrawer={this.props.detailState.isSlidingDrawerOpen}
                        customCloseButtonOptions={{ className: "btn btn-toxnot-primary", localizedText: this.localizeString("Done") }}>
                        {this.renderSlidingDrawerContent()}
                    </SlidingDrawer>
                }
            </>
        );
    }

    // ===============================================================================

    //private isSlidingDrawerContentLoading = () => {
    //    return true;
    //}

    // ===============================================================================

    private renderSlidingDrawerContent(): JSX.Element {
        if (this.props.detailState.isSlidingDrawerDataError) {
            return (
                <div className="text-center p-t-10">
                    <p>{this.localizeString("Sorry, there was a problem accessing the service. Please try again.")}</p>
                </div>
            )
        }

        const localState = this.props.getLocalState() as unknown as SharedMaterialsDatabaseStateController.SharedMaterialsState;
        const childProps = {
            isEmbedded: true,
            snapshotSurveyState: localState.sharedMaterialDetailState.snapshotSurveyState
        };

        return (
            <>
                {!this.props.detailState.isSlidingDrawerDataLoaded &&
                    <div className="text-center p-t-10">
                        <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                    </div>
                }
                {this.props.detailState.isSlidingDrawerDataLoaded &&
                    <CreateAndEditSurveyQuestionsPage {...childProps} />
                }
            </>
        );
    }

    // ===============================================================================

    private getSlidingDrawerTitle = (coreData: Models.SharedMaterialCoreData) => this.localizeString("Request Information for") + " " + coreData.materialName;

    // ===============================================================================

    private slidingDrawer_onClose = () => {
        this.props.updateDetailStateValue({ isSlidingDrawerOpen: false });
    };

    // ===============================================================================

    private sendSurvey = () => {
        const coreData = this.props.sharedMaterialsCoreDataMap[this.props.match.params.sharedMaterialId];
        const libraryInventoryId = this.props.detailState.detailBundle?.libraryInventoryId ?? null;

        this.props.sendRequestInformationSurvey(coreData, libraryInventoryId, this.getQueryParameter("accessCode"));
    };

    // ===============================================================================

    renderViewDetailsAttributes = (inventoryId: string, publicInventoryId: string, publicInventoryDataId: string, startId: string, startContext: string) => {
        // Checks if the DPP is unclaimed and verifies whether the user has added the Passport to their library.
        const coreData = this.props.sharedMaterialsCoreDataMap[this.props.match.params.sharedMaterialId];
        if (this.props.appServices.userContext && this.props.appServices.userContext.teamOwnerId !== coreData.ownerId) {
            if (this.props.detailState.detailBundle?.isUnClaimed && this.props.detailState.detailBundle?.libraryInventoryId === null) {
                return {
                    target: "_blank",
                    rel: "noopener noreferrer",
                    onClick: () => { $('#warn-about-adding-passport-to-library').modal('show'); },
                    style: { cursor: "pointer" }
                };
            }
        }
        return {
            href: `/Data/Inventory?inventoryId=${encodeURIComponent(inventoryId)}&publicInventoryId=${encodeURIComponent(publicInventoryId)}&publicInventoryDataId=${encodeURIComponent(publicInventoryDataId)}&mode=Passport&startControllerId=${encodeURIComponent(startId)}&startControllerContentContext=${encodeURIComponent(startContext)}`,
            target: "_blank",
            rel: "noopener noreferrer"
        };
    }

    // ===============================================================================

    protected isSharedMaterialFound(): boolean {
        return this.props.detailState.status === SharedMaterialsDatabaseStateController.SharedMaterialDetailStatus.Found;
    }

    // ===============================================================================

    //
    // UI Events
    //

    protected requestInformationButton_click = async () => {
        this.props.updateDetailStateValue({ isSlidingDrawerOpen: true });

        if (!this.props.detailState.isSlidingDrawerDataLoaded) {
            const coreData = this.props.sharedMaterialsCoreDataMap[this.props.match.params.sharedMaterialId];

            this.props.getPassportTemplate(coreData, this.getQueryParameter("accessCode"));
        }
    }

    // ===============================================================================

    protected toggleSection_click = (dataSectionId: string) => {
        this.props.toggleDetailDataSection(dataSectionId);
    }

    // ===============================================================================

    protected contactSupplierButton_click = (e: React.MouseEvent) => {
        this.props.appServices.trackEvent("exchange-contact-supplier-clicked");
        this.props.initializeRequest();
    }

    // ===============================================================================

    protected viewCompanyButton_click = (e: React.MouseEvent) => { }

    // ===============================================================================

    protected purchaseMaterial_click = (e: React.MouseEvent) => { }

    // ===============================================================================

    private shareMaterialButton_click = () => {
        this.props.appServices.trackEvent("exchange-share-passport-clicked");
        this.props.initializeRequest();
    }

    // ===============================================================================

    protected requestAccessButton_click = () => {
        this.props.appServices.trackEvent("exchange-request-access-to-passport-clicked");
        this.props.requestAccessToSharedMaterials([this.props.match.params.sharedMaterialId]);
        this.props.history.push(ToxnotReactComponentPaths.RequestAccessToSharedMaterial);
    }

    // ===============================================================================

    public addToLibraryButton_click = (sharedMaterialId: string) => {
        this.props.appServices.trackEvent("exchange-add-to-library-clicked");
        this.props.addToMyLibrary(sharedMaterialId);
    }

    // ===============================================================================

    private goToLibrarySourceInventoryButton_click = (publicInventoryId: string) => {
        this.props.goToSourceMaterial(publicInventoryId);
    }

    // ===============================================================================

    private goToLibraryInventoryButton_click = (libraryInventoryId: string) => {
        window.location.href = `/Data/Inventory?inventoryId=${encodeURIComponent(libraryInventoryId)}`;
    }
}

// ===============================================================================

export default connect(
    // Selects which state properties are merged into the component's props
    (state: ApplicationState) => ({
        detailState: state.sharedMaterialsState?.sharedMaterialDetailState,
        sharedMaterialsCoreDataMap: state.sharedMaterialsState?.sharedMaterialsCoreDataMap,
        appServices: state.appServices,
        isLoadingCatalog: state.sharedMaterialsState?.isLoadingCatalog
    }),
    SharedMaterialsDatabaseStateController.actionCreators // Selects which action creators are merged into the component's props
)(withRouter(SharedMaterialDetail) as any);
